import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type CreateInboundMailboxResult,
  type ListInboundMailboxesResult,
  type RecentInboundEmailResult,
} from '@whinerp/client';
import { Button, Field, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement, useSession } from '../../app/session.js';

/**
 * Inbound mailboxes (G-048): addresses whose mail becomes leads.
 *
 * The address token and signing secret are shown once, when the mailbox is
 * made, and the panel says so plainly - only the token's hash is kept, so a
 * lost token means a new mailbox, not a lookup.
 *
 * Each mailbox's recent mail is listed with what became of it and why, so
 * "I emailed and nothing happened" has an answer on this screen: an
 * out-of-office reply that was ignored, a reply filed on the lead it belonged
 * to, a person matched to an existing contact.
 */

type Mailbox = ListInboundMailboxesResult['mailboxes'][number];

function Recent({ mailboxId }: { mailboxId: string }): JSX.Element {
  const t = useT();
  const recent = useQuery<RecentInboundEmailResult, ApiError>({
    queryKey: ['inbound-recent', mailboxId],
    queryFn: () => api.recentInboundEmail({ path: { mailboxId } }),
  });
  if (recent.isPending) return <Skeleton className="h-8 w-full" />;
  if (recent.isError || recent.data.messages.length === 0) {
    return <p className="text-meta text-ink-meta">{t('inbound.recent.none')}</p>;
  }
  return (
    <ul className="flex flex-col gap-1 text-meta" data-inbound-recent={mailboxId}>
      {recent.data.messages.map((message) => (
        <li key={message.inboundId} data-inbound-outcome={message.outcome} className="flex flex-wrap gap-2">
          <span className="text-ink">{message.from}</span>
          <span className="text-ink-600">{message.subject ?? ''}</span>
          <span className="text-ink-meta">
            {t(`inbound.outcome.${message.outcome}`)}
            {message.reason ? ` — ${message.reason}` : ''}
          </span>
        </li>
      ))}
    </ul>
  );
}

export function InboundMailboxesPanel(): JSX.Element | null {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const enabled = useEntitlement('F-036');
  const { can } = useSession();
  const allowed = enabled.enabled && can('setup.modules.manage');

  const [label, setLabel] = useState('');
  // Enquiries become leads; support mail becomes cases (G-193).
  const [purpose, setPurpose] = useState<'leads' | 'cases'>('leads');
  const [created, setCreated] = useState<CreateInboundMailboxResult | null>(null);
  const [showing, setShowing] = useState<string | null>(null);

  const mailboxes = useQuery<ListInboundMailboxesResult, ApiError>({
    queryKey: ['inbound-mailboxes'],
    queryFn: () => api.listInboundMailboxes({}),
    enabled: allowed,
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['inbound-mailboxes'] });

  const create = useMutation<CreateInboundMailboxResult, ApiError>({
    mutationFn: () => api.createInboundMailbox({ body: { label: label.trim(), leadSource: null, purpose } }),
    onSuccess: (result) => {
      setCreated(result);
      setLabel('');
      refresh();
    },
  });

  const toggle = useMutation({
    mutationFn: (mailbox: Mailbox) =>
      api.setInboundMailboxActive({ path: { mailboxId: mailbox.mailboxId }, body: { active: !mailbox.active } }),
    onSuccess: refresh,
  });

  if (!allowed) return null;

  return (
    <Panel data-region="inbound-mailboxes">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('inbound.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('inbound.help')}</p>

        {created ? (
          /* Once. Only the hash is kept, so there is no showing it again. */
          <div className="flex flex-col gap-1 rounded-control bg-sunken p-3 text-dense" data-inbound-created>
            <p className="text-warning-text">{t('inbound.created.once')}</p>
            <span className="text-ink-meta">{t('inbound.created.url')}</span>
            <code className="break-all text-ink" data-inbound-url>{`/email-inbound/${created.token}`}</code>
            <span className="text-ink-meta">{t('inbound.created.secret')}</span>
            <code className="break-all text-ink" data-inbound-secret>{created.signingSecret}</code>
            <Button intent="secondary" size="small" onClick={() => setCreated(null)}>
              {t('inbound.created.done')}
            </Button>
          </div>
        ) : null}

        {mailboxes.isPending ? (
          <Skeleton className="h-12 w-full" />
        ) : mailboxes.isError ? (
          <p className="text-dense text-danger-text">{errorText(mailboxes.error)}</p>
        ) : mailboxes.data.mailboxes.length === 0 ? (
          <p className="text-dense text-ink-meta" data-inbound-empty>{t('inbound.empty')}</p>
        ) : (
          <ul className="flex flex-col gap-3">
            {mailboxes.data.mailboxes.map((mailbox) => (
              <li key={mailbox.mailboxId} data-inbound-mailbox={mailbox.mailboxId} className="flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2 text-dense">
                  <span className="text-ink">{mailbox.label}</span>
                  <span className="text-meta text-ink-meta" data-inbound-mailbox-purpose={mailbox.purpose}>{t(`inbound.purpose.${mailbox.purpose}`)}</span>
                  <span className="text-meta text-ink-meta">
                    {t(mailbox.active ? 'inbound.state.on' : 'inbound.state.off')} ·{' '}
                    {t('inbound.received', { values: { count: String(mailbox.received) } })}
                  </span>
                  <Button intent="secondary" size="small" data-inbound-toggle={mailbox.mailboxId}
                    onClick={() => toggle.mutate(mailbox)}>
                    {t(mailbox.active ? 'inbound.switchOff' : 'inbound.switchOn')}
                  </Button>
                  <Button intent="ghost" size="small" data-inbound-show={mailbox.mailboxId}
                    onClick={() => setShowing(showing === mailbox.mailboxId ? null : mailbox.mailboxId)}>
                    {t('inbound.recent')}
                  </Button>
                </div>
                {showing === mailbox.mailboxId ? <Recent mailboxId={mailbox.mailboxId} /> : null}
              </li>
            ))}
          </ul>
        )}

        <form
          className="flex flex-wrap items-end gap-2"
          data-inbound-form
          onSubmit={(event) => {
            event.preventDefault();
            if (label.trim()) create.mutate();
          }}
        >
          <Field label={t('inbound.label')} value={label} data-inbound-label
            onChange={(event) => setLabel(event.currentTarget.value)} />
          <label className="flex flex-col gap-1 text-meta text-ink-meta">
            {t('inbound.purpose')}
            <select className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink" data-inbound-purpose
              value={purpose} onChange={(event) => setPurpose(event.target.value as 'leads' | 'cases')}>
              <option value="leads">{t('inbound.purpose.leads')}</option>
              <option value="cases">{t('inbound.purpose.cases')}</option>
            </select>
          </label>
          <Button intent="primary" size="small" type="submit" data-inbound-create disabled={create.isPending}>
            {t('inbound.create')}
          </Button>
        </form>
        {create.error ? <p className="text-dense text-danger-text">{errorText(create.error)}</p> : null}
      </div>
    </Panel>
  );
}
